export const PLACEHOLDER = Object.freeze({
  id: 'placeholder',
});

export const ITEMS_TO_KEEP_BETWEEN_SCREENS = 10;

/**
 * Hard ceiling on the spare views kept per template.
 *
 * The pool is normally self limiting: a row releases its views when it scrolls out and takes them straight back
 * when it scrolls in. At 6 to 8 items per row a row's window is 8 to 10 views, so a few rows leaving at once
 * during fast scrolling leaves roughly 16 to 24 spares — comfortably under the cap. A Grid window is larger,
 * around 5 rows of 6, so leaving a grid heavy page can briefly push past the cap and trim a handful.
 *
 * Trimming takes from the front. Rows are returned to in the reverse order they were left, so the front of the
 * pool holds the views of the rows furthest from the current position — the least likely to be wanted next, and
 * therefore the right ones to give up.
 */
export const MAX_BUFFERED_VIEWS_PER_TEMPLATE = 64;

export const HOLDER = {};

/** template -> Map of content id to the buffered views showing it, so a content match costs no scan */
const CONTENT_INDEX = {};

export const PAGE_STATE = {
  LOADED: 'loaded',
  MISSING: 'missing',
  LOADING: 'loading',
};

export const getOrCreateTemplatesHolder = template => {
  if (!HOLDER[template]) {
    HOLDER[template] = [];
  }

  return HOLDER[template];
};

const getOrCreateContentIndex = template => {
  if (!CONTENT_INDEX[template]) {
    CONTENT_INDEX[template] = new Map();
  }

  return CONTENT_INDEX[template];
};

const addToContentIndex = view => {
  if (view.bufferedContentId === undefined) {
    return;
  }

  const index = getOrCreateContentIndex(view.template);
  const bucket = index.get(view.bufferedContentId);

  if (bucket) {
    bucket.push(view);
  } else {
    index.set(view.bufferedContentId, [view]);
  }
};

const dropFromContentIndex = view => {
  if (!view || view.bufferedContentId === undefined) {
    return;
  }

  const index = CONTENT_INDEX[view.template];
  const bucket = index && index.get(view.bufferedContentId);

  if (!bucket) {
    return;
  }

  const at = bucket.indexOf(view);

  if (at >= 0) {
    bucket.splice(at, 1);
  }

  if (!bucket.length) {
    index.delete(view.bufferedContentId);
  }
};

/**
 * Takes a view out of the buffer, preferring one that already displays the requested content.
 * Such a view is usually the one released by the very same row, so it is still parented there and keeps the
 * item it was showing, which turns re-applying the data into a no-op instead of a full re-render
 *
 * @param {string} template content template identifier
 * @param {*} contentId identifier of the content the view is about to display
 * @returns {?Object} a buffered view, or null when the buffer holds nothing for this template
 */
export const getFromBuffer = (template, contentId = undefined) => {
  const holder = getOrCreateTemplatesHolder(template);

  if (!holder.length) {
    return null;
  }

  if (contentId !== undefined) {
    const index = CONTENT_INDEX[template];
    const bucket = index && index.get(contentId);

    // NOTE: taken from the back, the most recently released first. The holder has the final say, so an entry the
    // index has gone stale on is discarded rather than handed out
    while (bucket && bucket.length) {
      const view = bucket.pop();
      const at = holder.indexOf(view);

      if (!bucket.length) {
        index.delete(contentId);
      }

      if (at >= 0) {
        holder.splice(at, 1);

        return view;
      }
    }
  }

  const view = holder.pop();
  dropFromContentIndex(view);

  return view;
};

const pendingDetach = [];

// Views are released from _inactive, i.e. while Lightning walks the parent's children, so removing them right away
// would shrink the array under that loop
const flushPendingDetach = () => {
  while (pendingDetach.length) {
    const view = pendingDetach.pop();

    if (view.parent && !getOrCreateTemplatesHolder(view.template).includes(view)) {
      view.parent.childList.remove(view);
    }
  }
};

const scheduleDetach = view => {
  if (!pendingDetach.length) {
    Promise.resolve().then(flushPendingDetach);
  }

  pendingDetach.push(view);
};

export const backToBuffer = view => {
  if (!view || !view.template) {
    return;
  }

  const holder = getOrCreateTemplatesHolder(view.template);

  if (holder.includes(view)) {
    return;
  }

  // Remember what the view displays so it could be handed back for the same content later on
  view.bufferedContentId = view.item ? view.item.id : undefined;

  holder.push(view);
  addToContentIndex(view);

  // Trimmed from the front: getFromBuffer takes from the back, so the oldest entries are the least likely to be
  // wanted again. Detaching them lets the whole subtree go rather than keeping it parked in the pool
  while (holder.length > MAX_BUFFERED_VIEWS_PER_TEMPLATE) {
    const dropped = holder.shift();

    dropFromContentIndex(dropped);

    if (dropped && dropped.parent) {
      scheduleDetach(dropped);
    }
  }
};

/**
 * Drops a detached view from the pool, keeping ITEMS_TO_KEEP_BETWEEN_SCREENS of them so the next screen still has
 * something to reuse.
 *
 * NOTE: the entry is spliced out in place. Replacing `HOLDER[template]` with a filtered copy meant that a caller
 * holding a reference to the array — `backToBuffer` while it trims, which can detach a view and re-enter here —
 * would carry on mutating an array that is no longer the pool.
 *
 * @param {Object} view a view that has just been detached
 */
export const cleanupBuffer = view => {
  if (!view || !view.template) {
    return;
  }

  const holder = HOLDER[view.template];

  if (!holder || holder.length <= ITEMS_TO_KEEP_BETWEEN_SCREENS) {
    return;
  }

  const index = holder.indexOf(view);

  if (index >= 0) {
    holder.splice(index, 1);
    dropFromContentIndex(view);
  }
};

/**
 * Total number of views parked in the buffer, per template and overall. Exposed for the performance monitor.
 *
 * @returns {Object} `{ total, byTemplate }`
 */
export const getBufferStats = () => {
  const byTemplate = {};
  let total = 0;

  Object.keys(HOLDER).forEach(template => {
    const count = Array.isArray(HOLDER[template]) ? HOLDER[template].length : 0;
    byTemplate[template] = count;
    total += count;
  });

  return { total, byTemplate };
};

export const cleanupVirtualizationCache = () => {
  Object.keys(HOLDER).forEach(key => {
    if (Array.isArray(HOLDER[key])) {
      HOLDER[key].forEach(view => {
        if (view && view.parent) {
          view.parent.childList.remove(view);
        }
      });
    }

    delete HOLDER[key];
    delete CONTENT_INDEX[key];
  });
};
